import {
  Alert,
  Box,
  Button,
  Card,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  LinearProgress,
  Typography,
  Stack,
  Chip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Grid from "@mui/material/Grid2";
import {
  useCustom,
  useModal,
  useCustomMutation,
  useInvalidate,
} from "@refinedev/core";
import { useParams } from "react-router";
import { LocalShipping, Warehouse, WarningAmber } from "@mui/icons-material";

import { useTranslation } from "../../hooks/useTranslation";
import { OrderDetail } from "./types";
import { CopiableChip } from "../../components/ui/CopiableChip";

import { AddressEditModal } from "./components/address-edit/AddressEditModal";
import { PrepareShipmentModal } from "./components/prepare-shipment/PrepareShipmentModal";
import { ShipmentRow } from "./components/shipment-row/ShipmentRow";
import { SupplyOrderRow } from "./components/supplyOrder-row/SupplyOrderRow";
import { OrderItemsTable } from "./components/order-items/OrderItemsTable";
import { BuyerInfoCard } from "./components/buyer-info/BuyerInfoCard";
import { PackageInfoCard } from "./components/package-info/PackageInfoCard";
import { ArrivedToWarehouseModal } from "./components/arrived-to-warehouse/ArrivedToWarehouseModal";
import { WarehouseMessages } from "./components/warehouse-messages/WarehouseMessages";

import { mockOrderDetail } from "./mockOrderDetail";
import { OrderNotFound } from "./components/OrderNotFound";
import { ArrivedWarehousePayload } from "./components/arrived-to-warehouse/types";

export const OrderDetails = () => {
  /* =========================================================
     HOOKS — MUST BE TOP LEVEL
  ========================================================= */

  const { translate } = useTranslation("pages.orderDetails");
  const { orderId } = useParams<{ orderId: string }>();
  const queryClient = useQueryClient();
  const orderQueryKey = ["order-detail", orderId];

  const prepareShipmentModal = useModal();
  const addressEditModal = useModal();
  const arrivedWarehouseModal = useModal();

  const invalidate = useInvalidate();
  const { mutate: receiveToWarehouse, isLoading: isReceiving } =
    useCustomMutation();
  const { mutate: sendWarehouseMessage, isLoading: isSendingWarehouseMessage } =
    useCustomMutation();

  const USE_MOCK = false;

  const { data: realResponse, isLoading: isOrderLoading } =
    useCustom<OrderDetail>({
      url: `orders/${orderId}`,
      method: "get",
      queryOptions: {
        enabled: !!orderId && !USE_MOCK,
        queryKey: orderQueryKey,
      },
    });

  /* =========================================================
     DATA RESOLUTION (NO HOOKS BELOW)
  ========================================================= */

  const orderResponse = USE_MOCK ? { data: mockOrderDetail } : realResponse;

  const order = orderResponse?.data;
  const [isCriticalWarningOpen, setIsCriticalWarningOpen] = useState(false);
  const [isBypassWarningOpen, setIsBypassWarningOpen] = useState(false);

  const lastRelevantMessage = order?.warehouseMessages
    .filter(
      (m) =>
        m.message === "Now can send to customer" ||
        m.message === "Don't send to customer",
    )
    .at(-1);

  const hasNowCanSendWarning =
    lastRelevantMessage?.message === "Now can send to customer";

  const hasDoNotSendWarning =
    lastRelevantMessage?.message === "Don't send to customer";

  useEffect(() => {
    if (hasNowCanSendWarning) {
      setIsBypassWarningOpen(true);
    } else if (hasDoNotSendWarning) {
      setIsCriticalWarningOpen(true);
    }
  }, [hasDoNotSendWarning, hasNowCanSendWarning]);

  /* =========================================================
     EARLY RETURNS (SAFE)
  ========================================================= */

  if (!USE_MOCK && isOrderLoading) {
    return <LinearProgress />;
  }

  if (!order) {
    return (
      <Container maxWidth="xl">
        <OrderNotFound />
      </Container>
    );
  }

  /* =========================================================
     DERIVED
  ========================================================= */

  const dimensions = {
    length: order.estimatedLength,
    width: order.estimatedWidth,
    height: order.estimatedHeight,
    weight: order.estimatedWeight,
  };

  const allItemsFullySent = order.orderItems.every(
    (item) => item.receivedQuantity === item.quantityShipped,
  );

  const totalQuantity = order.orderItems.reduce(
    (sum, item) => sum + item.quantity,
    0,
  );
  const distinctProductCount = order.orderItems.length;

  /* =========================================================
     HANDLERS
  ========================================================= */

  const handleArrivedSubmit = (payload: ArrivedWarehousePayload[]) => {
    receiveToWarehouse(
      {
        url: `orders/${order.barcode}/received`,
        method: "put",
        values: payload,
      },
      {
        onSuccess: () => {
          arrivedWarehouseModal.close();

          invalidate({
            resource: "orders",
            id: order.externalId,
            invalidates: ["detail"],
          });
        },
      },
    );
  };

  const handleSendWarehouseMessage = async (message: string) => {
    await new Promise<void>((resolve, reject) => {
      sendWarehouseMessage(
        {
          url: `orders/${order.barcode}/warehouse-messages`,
          method: "post",
          values: {
            message,
            author: translate("warehouseMessages.me", {
              defaultValue: "WAREHOUSE",
            }),
            direction: "WAREHOUSE",
          },
        },
        {
          onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: orderQueryKey });
            resolve();
          },
          onError: () => reject(new Error("send-warehouse-message-failed")),
        },
      );
    });
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <Container
      component={Card}
      sx={{
        p: 2,
        border:
          hasNowCanSendWarning || hasDoNotSendWarning ? "2px solid" : undefined,
        borderColor: hasNowCanSendWarning
          ? "success.main"
          : hasDoNotSendWarning
            ? "error.main"
            : undefined,
      }}
      maxWidth="xl"
    >
      {hasNowCanSendWarning && (
        <Alert severity="success" variant="filled" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {translate("bypassWarning.title")}
          </Typography>
          <Typography variant="body2">
            {translate("bypassWarning.description")}
          </Typography>
        </Alert>
      )}
      {!hasNowCanSendWarning && hasDoNotSendWarning && (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            {translate("criticalWarning.title")}
          </Typography>
          <Typography variant="body2">
            {translate("criticalWarning.description")}
          </Typography>
        </Alert>
      )}

      {/* ================= HEADER ================= */}
      <Stack direction="row" spacing={1} alignItems="center" mb={2}>
        <Box
          display="grid"
          gridTemplateColumns="auto auto"
          alignItems="center"
          columnGap={1}
          rowGap={1.25}
          mb={2}
          justifyContent="start"
        >
          <Typography variant="h6" fontWeight={600}>
            {translate("status.title")}:
          </Typography>
          <Chip
            color={
              hasNowCanSendWarning
                ? "success"
                : hasDoNotSendWarning
                  ? "error"
                  : "primary"
            }
            label={translate(`status.${order.status}`, {
              defaultValue: order.status,
            })}
            sx={{
              fontWeight: 600,
              height: 22,
              "& .MuiChip-label": {
                px: 1,
                fontWeight: 600,
                textTransform: "capitalize",
              },
            }}
          />

          <Typography variant="h6" fontWeight={600}>
            {translate("title")}
          </Typography>
          <CopiableChip initialLabel={orderId ?? "-"} />

          {order.storeName && (
            <>
              <Typography variant="h6" fontWeight={600}>
                {translate("orderInfo.storeName")}:
              </Typography>
              <CopiableChip initialLabel={order.storeName} />
            </>
          )}
        </Box>

        <Box flex={1} />

        {/* ARRIVED TO WAREHOUSE */}
        <Button
          variant="contained"
          size="small"
          color="success"
          sx={{ textTransform: "none" }}
          startIcon={<Warehouse fontSize="small" />}
          onClick={arrivedWarehouseModal.show}
        >
          {translate("arrivedToWarehouse.title")}
        </Button>

        {/* PREPARE SHIPMENT */}
        {!allItemsFullySent && (
          <Button
            variant="contained"
            size="small"
            color={hasDoNotSendWarning ? "error" : "primary"}
            sx={{ textTransform: "none" }}
            disabled={hasDoNotSendWarning}
            onClick={prepareShipmentModal.show}
          >
            <LocalShipping fontSize="small" sx={{ mr: 1 }} />
            {translate("prepareShipment.title")}
          </Button>
        )}
      </Stack>

      {/* ================= MULTI-ITEM WARNING ================= */}
      {(distinctProductCount > 1 || totalQuantity > 1) && (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {distinctProductCount > 1 && (
            <Typography variant="body2">
              {translate("multiItemWarning.multipleTypes", {
                count: distinctProductCount,
              })}
            </Typography>
          )}
          {totalQuantity > 1 && (
            <Typography variant="body2">
              {translate("multiItemWarning.totalQuantity", {
                count: totalQuantity,
              })}
            </Typography>
          )}
        </Alert>
      )}

      {/* ================= ITEMS ================= */}
      <Box
        sx={{
          border: hasDoNotSendWarning ? "1px solid" : undefined,
          borderColor: hasNowCanSendWarning
            ? "success.main"
            : hasDoNotSendWarning
              ? "error.main"
              : undefined,
          borderRadius: hasDoNotSendWarning ? 1 : undefined,
          p: hasDoNotSendWarning ? 1 : undefined,
        }}
      >
        <OrderItemsTable
          items={order.orderItems}
          currencyCode={order.currencyCode}
        />
      </Box>

      <Grid container spacing={2} mt={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <BuyerInfoCard
            shippingAddress={order.shippingAddress}
            onEdit={addressEditModal.show}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <PackageInfoCard
            dimensions={dimensions}
            totalDesi={order.estimatedTotalDesi}
            estimatedCost={order.estimatedCost}
          />
        </Grid>
        {order.shipments.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={3}>
              {order.shipments.map((shipment) => (
                <ShipmentRow
                  key={shipment.id}
                  shipment={shipment}
                  barcode={order.barcode}
                  orderStatus={order.status}
                />
              ))}
            </Stack>
          </Grid>
        )}

        <Grid size={{ xs: 12, md: 6 }}>
          <WarehouseMessages
            messages={order.warehouseMessages}
            onSendMessage={handleSendWarehouseMessage}
            isSending={isSendingWarehouseMessage}
          />
        </Grid>
        {order.supplyOrders.length > 0 && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={3}>
              {order.supplyOrders.map((supplyOrder) => (
                <SupplyOrderRow
                  key={supplyOrder.id}
                  supplyOrder={supplyOrder}
                />
              ))}
            </Stack>
          </Grid>
        )}
      </Grid>

      {/* ================= MODALS ================= */}
      <PrepareShipmentModal
        open={prepareShipmentModal.visible}
        onClose={prepareShipmentModal.close}
        orderDetails={order}
      />

      <AddressEditModal
        open={addressEditModal.visible}
        onClose={addressEditModal.close}
        shippingAddress={order.shippingAddress}
        barcode={order.barcode}
      />

      <ArrivedToWarehouseModal
        open={arrivedWarehouseModal.visible}
        onClose={arrivedWarehouseModal.close}
        order={order}
        loading={isReceiving}
        onSubmit={handleArrivedSubmit}
      />

      <Dialog
        open={isCriticalWarningOpen}
        onClose={() => setIsCriticalWarningOpen(false)}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              width: "100%",
              maxWidth: 820,
              borderRadius: 2,
              p: 1,
              border: "2px solid",
              borderColor: "error.main",
            },
          },
        }}
      >
        <DialogContent>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
            <WarningAmber sx={{ fontSize: 44, color: "error.main" }} />
            <Typography variant="h5" color="error.main" fontWeight={800}>
              {translate("criticalWarning.popupTitle")}
            </Typography>
          </Stack>

          <Typography variant="subtitle1" color="error.main" fontWeight={600}>
            {translate("criticalWarning.description")}
          </Typography>
          <Typography variant="body1" color="text.primary" mt={1.5}>
            {translate("criticalWarning.popupDescription")}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            color="error"
            onClick={() => setIsCriticalWarningOpen(false)}
          >
            {translate("criticalWarning.acknowledge")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isBypassWarningOpen}
        onClose={() => setIsBypassWarningOpen(false)}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            sx: {
              width: "100%",
              maxWidth: 820,
              borderRadius: 2,
              p: 1,
              border: "2px solid",
              borderColor: "success.main",
            },
          },
        }}
      >
        <DialogContent>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
            <WarningAmber sx={{ fontSize: 44, color: "success.main" }} />
            <Typography variant="h5" color="success.main" fontWeight={800}>
              {translate("bypassWarning.popupTitle")}
            </Typography>
          </Stack>

          <Typography variant="subtitle1" color="success.main" fontWeight={600}>
            {translate("bypassWarning.description")}
          </Typography>
          <Typography variant="body1" color="text.primary" mt={1.5}>
            {translate("bypassWarning.popupDescription")}
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            variant="contained"
            color="success"
            onClick={() => setIsBypassWarningOpen(false)}
          >
            {translate("bypassWarning.acknowledge")}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
